import React, { createContext, useEffect, useMemo, useState } from 'react';
import AppLayout from '../../../generic-components/Layout';
import ContentWrapper from '../../../generic-components/ContentWrapper';
import { useInvoiceCreationMutation } from '../../../hooks/mutations/useInvoiceCreationMutation';
import { useStore } from '../../../store';
import { IInvoiceReminder } from './types/types';
import { CreationNavChildren } from './components/CreationNavChildren';

import { CREATION_CONTENT_TYPE, STYLES } from './consts/consts';
import { getIframeUrl } from './utils';
import { CreationContentSwitch } from './components/CreationContentSwitch';
import { useUpdateExistingInvoice } from './hooks/useUpdateExistingInvoice';

interface ICreationContext {
  iFrameUrl: string;
  mutateInvoiceReminders?: (data: IInvoiceReminder[]) => void;
  mutateInvoiceCreationStatus?: (data: boolean) => void;
  isLoading: boolean;
  reminders: IInvoiceReminder[];
  isInvoiceCreated: boolean;
}

export const CreationContext = createContext<ICreationContext>({
  iFrameUrl: '',
  isLoading: false,
  reminders: [],
  isInvoiceCreated: false,
});

export const InvoiceCreation = () => {
  const {
    customer: { externalId },
  } = useStore((state) => state.summary);

  const setInvoiceData = useStore((state) => state.setInvoiceData);

  const invoiceData = useStore((state) => state.invoiceData);

  const invoiceDataToUpdate = useStore((state) => state.invoiceDataToUpdate);

  const setInitialInvoiceState = useStore((state) => state.setInitialInvoiceState);

  const [isInvoiceCreated, setIsInvoiceCreated] = useState<boolean>(false);
  const {
    mutate,
    isLoading: isLoadingInvoiceCreation,
    isError: isCreateInvoiceError,
  } = useInvoiceCreationMutation({
    customerId: externalId,
    onSuccess: async (data: any) => {
      setInvoiceData(data);
    },
  });

  const { mutate: updateInvoice, isLoading: isUpdateInvoiceData } = useUpdateExistingInvoice((data: any) => {
    setInvoiceData(data);
  });

  useEffect(() => {
    if (!invoiceDataToUpdate || isUpdateInvoiceData) return;
    updateInvoice({ customerId: externalId, invoicePayload: invoiceDataToUpdate, invoiceId: invoiceData?.externalId });
  }, [invoiceDataToUpdate]);

  const creationBodyContentType = useMemo(() => {
    if (isCreateInvoiceError) {
      return CREATION_CONTENT_TYPE.ERROR;
    }
    if (isLoadingInvoiceCreation || !invoiceData) {
      return CREATION_CONTENT_TYPE.LOADER;
    }
    return CREATION_CONTENT_TYPE.CONTENT;
  }, [isCreateInvoiceError, isLoadingInvoiceCreation, invoiceData]);

  const contextValue = useMemo(
    () => ({
      reminders: invoiceData?.invoiceReminders || [],
      isInvoiceCreated,
      mutateInvoiceCreationStatus: (status: boolean) => setIsInvoiceCreated(status),
      iFrameUrl: getIframeUrl(invoiceData?.externalId),
      isLoading: isUpdateInvoiceData,
    }),
    [invoiceData, isUpdateInvoiceData, isInvoiceCreated],
  );

  useEffect(() => {
    setInitialInvoiceState();
    mutate();
  }, []);

  return (
    <div>
      <CreationContext.Provider value={contextValue}>
        <AppLayout
          isWizardMode
          layoutStyle={STYLES.INVOICE_CREATION.APP_LAYOUT}
          navBarStyle={STYLES.INVOICE_CREATION.APP_LAYOUT_NAV_BAR}
          navChildren={<CreationNavChildren isLoading={isLoadingInvoiceCreation} />}
          chatBotStyle='absolute top-[84vh]  mb-[100px] left-[1305px] pc-sm:left-[1305px] pc-md-lg:left-[1441px] pc-lg:left-[1794px] z-40 w-[46px] h-[46px] rounded-full bg-action03 flex items-center justify-center cursor-pointer shadow-helpMenuButton hover:bg-action04'
        >
          <ContentWrapper>
            <CreationContentSwitch contentType={creationBodyContentType} />
          </ContentWrapper>
        </AppLayout>
      </CreationContext.Provider>
    </div>
  );
};
